/**
 * ServiceOverviewMap — Draggable node map: CIs → Service → Dependencies.
 * Nodes can be freely moved with drag & drop. Auto-layout on first render.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

const NODE_W = 190;
const NODE_H = 54;
const NODE_SVC_W = 210;
const NODE_SVC_H = 120;

const COL_HOST = 'var(--accent)';
const COL_SVC  = 'var(--accent)';
const COL_DEP_OUT = '#f59e0b';
const COL_DEP_IN  = '#10b981';
// Relaciones tipadas del Diseñador ("se autentica en", "proporciona"...):
// viven en otra tabla que las dependencias declaradas y antes no se pintaban.
const COL_REL   = '#8b5cf6';

const CI_ICONS = {
  rack_element: '🖥️', virt_vm: '💻', printer: '🖨️',
  wifi_controller: '📡', firewall: '🛡️', router: '🌐', workstation: '🖥️',
};
const REL_ICONS = {
  ...CI_ICONS,
  service: '🧩', wifi_access_point: '📶', storage_lun: '💽',
  server_volume: '📂', isp_circuit: '🛰️', ipam_ip: '🔢',
  logical_component: '🗂️',
};
const ACC_ICONS = {
  ssh: '🔒', rdp: '🖥️', https: '🌐', http: '🌐',
  terminal_server: '🖥️', api: '🔌', database: '🗄️', console: '🖥️', vpn: '🔐',
};
const DEP_ICONS = {
  uses_db: '🗄️', authenticated_by: '🔐', proxied_by: '🔁',
  depends_on: '⬅️', uses_cache: '⚡', uses_queue: '📨',
  replicates_to: '📋', backed_up_by: '💾', monitored_by: '📊',
  resolves_via: '🌐', stores_to: '📦',
};

function curvedPath(x1, y1, x2, y2) {
  const mx = (x1 + x2) / 2;
  return `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`;
}

/** Punta de flecha acorde al color de la relación. */
function markerFor(color) {
  if (color === COL_REL) return 'url(#mv-arr-rel)';
  if (color === COL_DEP_IN) return 'url(#mv-arr-used)';
  return 'url(#mv-arr-need)';
}

function nearestEdge(x1, y1, x2, y2, w, h) {
  // Find the closest edge point on a w×h rect at (x2,y2) from (x1,y1)
  const cx = x2 + w / 2, cy = y2 + h / 2;
  const dx = x1 - cx, dy = y1 - cy;
  const ax = Math.abs(dx), ay = Math.abs(dy);
  if (ax * h > ay * w) {
    return { x: dx > 0 ? x2 + w : x2, y: cy + (dy / ax) * (w / 2) };
  } else {
    return { x: cx + (dx / ay) * (h / 2), y: dy > 0 ? y2 + h : y2 };
  }
}

const NOMBRE_LOGICO_GENERICO = /^(Componente lógico|logical_component)\s*#?-?\d*$/i;

/** Recorta sin partir paréntesis ni palabras: «Copia de seguridad (backup)» → «Copia de seguridad…». */
function shortName(value, max = 24) {
  const name = String(value || '').trim();
  if (name.length <= max) return name;
  let cut = name.slice(0, max);
  const open = cut.lastIndexOf('(');
  if (open > 0 && !cut.includes(')', open)) cut = cut.slice(0, open);
  return `${cut.trimEnd()}…`;
}

/** Un componente lógico sin `logical_label` no es un nombre: es una referencia. */
function esComponenteSinNombre(r) {
  return r.other_ci_type === 'logical_component'
    && (!r.other_name || NOMBRE_LOGICO_GENERICO.test(String(r.other_name).trim()));
}

function layoutNodes(hosts, outNodes, inNodes) {
  const positions = {};
  let y = 60;
  hosts.forEach((h, i) => {
    positions[`host-${h.id}`] = { x: 30, y: y };
    y += NODE_H + 20;
  });
  const svcY = Math.max(y + 40, 200);
  // Si no hay alojamiento, el servicio y sus dependencias se desplazan a la
  // izquierda: así el mapa no deja una columna vacía enorme a la izquierda.
  const columnShift = hosts.length ? 0 : -260;
  positions['service'] = { x: 290 + columnShift, y: svcY };
  y = 60;
  outNodes.forEach((d, i) => {
    positions[d.key] = { x: 550 + columnShift, y: y };
    y += NODE_H + 14;
  });
  y = Math.max(y + 30, svcY + NODE_SVC_H + 40);
  inNodes.forEach((d, i) => {
    positions[d.key] = { x: 550 + columnShift, y: y };
    y += NODE_H + 14;
  });
  return positions;
}

/**
 * Junta las dos fuentes de relaciones de un servicio en dos listas listas para
 * pintar:
 *   · dependencias declaradas (`dependencies_out/in`) — tabla clásica,
 *   · relaciones del Diseñador (`relations_out/in`) — las del canvas, con su
 *     tipo en español y con el otro extremo ya resuelto a nombre.
 */
function buildNodeLists(detail) {
  const outNodes = [
    ...(detail.dependencies_out || []).map(d => ({
      key: `dep-out-${d.id}`,
      name: d.to_service_display_name || d.to_service_name || `#${d.to_service_id}`,
      sub: `${d.dep_type_label || d.dep_type}${d.port ? ` :${d.port}` : ''}`,
      icon: DEP_ICONS[d.dep_type] || '⬅️',
      color: COL_DEP_OUT,
      serviceId: d.to_service_id,
    })),
    ...(detail.relations_out || []).filter(r => !esComponenteSinNombre(r)).map(r => ({
      key: `rel-out-${r.id}`,
      name: r.other_name || 'Componente lógico',
      sub: r.relation_label || r.relation,
      icon: r.other_icon || REL_ICONS[r.other_kind] || '🧩',
      detail: r.other_detail,
      color: COL_REL,
      serviceId: r.other_service_id,
    })),
  ];
  const inNodes = [
    ...(detail.dependencies_in || []).map(d => ({
      key: `dep-in-${d.id}`,
      name: d.from_service_display_name || d.from_service_name || `#${d.from_service_id}`,
      sub: d.dep_type_label || d.dep_type,
      icon: DEP_ICONS[d.dep_type] || '➡️',
      color: COL_DEP_IN,
      serviceId: d.from_service_id,
      dashed: true,
    })),
    ...(detail.relations_in || []).filter(r => !esComponenteSinNombre(r)).map(r => ({
      key: `rel-in-${r.id}`,
      name: r.other_name || 'Componente lógico',
      sub: r.relation_label || r.relation,
      icon: r.other_icon || REL_ICONS[r.other_kind] || '🧩',
      detail: r.other_detail,
      color: COL_REL,
      serviceId: r.other_service_id,
    })),
  ];
  return { outNodes, inNodes };
}

export default function ServiceOverviewMap({ serviceId, companyId, onOpenService }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [positions, setPositions] = useState({});
  const [dragging, setDragging] = useState(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const svgRef = useRef(null);

  const load = useCallback(async () => {
    if (!serviceId || !companyId) return;
    setLoading(true);
    try {
      const svc = await api.getService(serviceId, companyId);
      setDetail(svc);
      const hosts = svc.hosts || [];
      const { outNodes, inNodes } = buildNodeLists(svc);
      setPositions(layoutNodes(hosts, outNodes, inNodes));
    } catch (_) {
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [serviceId, companyId]);

  useEffect(() => { load(); }, [load]);

  const handleMouseDown = (id, e) => {
    e.preventDefault();
    const rect = svgRef.current.getBoundingClientRect();
    const pos = positions[id];
    setDragging(id);
    setOffset({ x: e.clientX - rect.left - pos.x, y: e.clientY - rect.top - pos.y });
  };

  const handleMouseMove = (e) => {
    if (!dragging || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    setPositions(prev => ({
      ...prev,
      [dragging]: {
        x: e.clientX - rect.left - offset.x,
        y: e.clientY - rect.top - offset.y,
      }
    }));
  };

  const handleMouseUp = () => {
    setDragging(null);
  };

  if (loading) return <div style={{ padding: 30, color: 'var(--text-muted)', textAlign: 'center' }}>Cargando vista general…</div>;
  if (!detail) return <div style={{ padding: 30, color: 'var(--text-muted)', textAlign: 'center' }}>No se pudo cargar.</div>;

  const hosts = detail.hosts || [];
  const accessPoints = detail.access_points || [];
  const { outNodes, inNodes } = buildNodeLists(detail);
  const unnamedLogical = [
    ...(detail.relations_out || []),
    ...(detail.relations_in || []),
  ].filter(esComponenteSinNombre).length;

  const allPos = Object.values(positions);
  const hasRelations = outNodes.length > 0 || inNodes.length > 0;
  const minX = hosts.length ? 800 : 560;
  const maxX = Math.max(...allPos.map(p => p.x + NODE_W + 50), minX);
  const maxY = Math.max(...allPos.map(p => p.y + NODE_H + 50), 500);

  return (
    <div className="som-panel">
      {(!hosts.length || !hasRelations || unnamedLogical > 0) && (
        <div className="som-notices">
          {unnamedLogical > 0 && (
            <div className="som-notice">
              <InformationCircleIcon className="som-notice-icon" />
              <strong>{unnamedLogical} componente{unnamedLogical === 1 ? '' : 's'} lógico{unnamedLogical === 1 ? '' : 's'} sin nombre</strong>
              <span>{unnamedLogical === 1 ? 'Nómbralo en el diseñador' : 'Nómbralos en el diseñador'}</span>
            </div>
          )}
          {!hosts.length && (
            <div className="som-notice">
              <InformationCircleIcon className="som-notice-icon" />
              <strong>Sin alojamiento asignado</strong>
              <span>Configúralo en Alojamiento</span>
            </div>
          )}
          {!hasRelations && (
            <div className="som-notice">
              <InformationCircleIcon className="som-notice-icon" />
              <strong>Sin dependencias registradas</strong>
              <span>Añádelas desde el diseñador</span>
            </div>
          )}
        </div>
      )}
      <div className="som-wrap" style={{ overflow: 'auto', width: '100%', padding: '8px 0', cursor: dragging ? 'grabbing' : 'grab' }}
        onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
      <svg ref={svgRef} width={maxX} height={maxY} viewBox={`0 0 ${maxX} ${maxY}`}
        style={{ minWidth: maxX, minHeight: maxY, display: 'block' }}>
        <defs>
          <marker id="mv-arr-host" markerWidth="10" markerHeight="8" refX="10" refY="4" orient="auto">
            <path d="M0,0 L10,4 L0,8 Z" fill={COL_HOST} />
          </marker>
          <marker id="mv-arr-need" markerWidth="10" markerHeight="8" refX="10" refY="4" orient="auto">
            <path d="M0,0 L10,4 L0,8 Z" fill={COL_DEP_OUT} />
          </marker>
          <marker id="mv-arr-used" markerWidth="10" markerHeight="8" refX="10" refY="4" orient="auto">
            <path d="M0,0 L10,4 L0,8 Z" fill={COL_DEP_IN} />
          </marker>
          <marker id="mv-arr-rel" markerWidth="10" markerHeight="8" refX="10" refY="4" orient="auto">
            <path d="M0,0 L10,4 L0,8 Z" fill={COL_REL} />
          </marker>
          <filter id="mv-shad">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.15" />
          </filter>
        </defs>

        <rect x={10} y={10} width={maxX - 20} height={maxY - 20} rx={12}
          fill="var(--bg-toolbar)" stroke="var(--border)" strokeWidth="1" />

        {/* ── Arrows (drawn first so they appear behind nodes) ── */}
        {hosts.map((h, i) => {
          const p = positions[`host-${h.id}`];
          const sp = positions['service'];
          if (!p || !sp) return null;
          const e1 = { x: p.x + NODE_W, y: p.y + NODE_H / 2 };
          const e2 = nearestEdge(e1.x, e1.y, sp.x, sp.y, NODE_SVC_W, NODE_SVC_H);
          return <path key={`arr-h-${h.id}`} d={curvedPath(e1.x, e1.y, e2.x, e2.y)}
            stroke={COL_HOST} strokeWidth="2" fill="none" markerEnd="url(#mv-arr-host)" strokeDasharray="5,4" />;
        })}
        {outNodes.map((d) => {
          const dp = positions[d.key];
          const sp = positions['service'];
          if (!dp || !sp) return null;
          const e1 = nearestEdge(dp.x + NODE_W / 2, dp.y + NODE_H / 2, sp.x, sp.y, NODE_SVC_W, NODE_SVC_H);
          const e2 = { x: dp.x, y: dp.y + NODE_H / 2 };
          return <path key={`arr-${d.key}`} d={curvedPath(e1.x, e1.y, e2.x, e2.y)}
            stroke={d.color} strokeWidth="2" fill="none" markerEnd={markerFor(d.color)}
            strokeDasharray={d.dashed ? '5,4' : undefined} />;
        })}
        {inNodes.map((d) => {
          const dp = positions[d.key];
          const sp = positions['service'];
          if (!dp || !sp) return null;
          const e1 = { x: dp.x + NODE_W, y: dp.y + NODE_H / 2 };
          const e2 = nearestEdge(e1.x, e1.y, sp.x, sp.y, NODE_SVC_W, NODE_SVC_H);
          return <path key={`arr-${d.key}`} d={curvedPath(e1.x, e1.y, e2.x, e2.y)}
            stroke={d.color} strokeWidth="2" fill="none" markerEnd={markerFor(d.color)}
            strokeDasharray={d.dashed ? '5,4' : undefined} />;
        })}

        {/* ── HOST NODES ── */}
        {hosts.map((h, i) => {
          const p = positions[`host-${h.id}`];
          if (!p) return null;
          return (
            <g key={`host-${h.id}`} onMouseDown={(e) => handleMouseDown(`host-${h.id}`, e)}
              style={{ cursor: 'move' }}>
              <title>{`${h.ci_name || `#${h.ci_id}`} · ${h.ci_type_label || h.ci_type}${h.role ? ` · ${h.role}` : ''}`}</title>
              <rect x={p.x} y={p.y} width={NODE_W} height={NODE_H} rx={8}
                fill="var(--bg-card)" stroke={dragging === `host-${h.id}` ? COL_HOST : COL_HOST}
                strokeWidth={dragging === `host-${h.id}` ? 3 : 2} filter="url(#mv-shad)" />
              <text x={p.x + 14} y={p.y + NODE_H / 2 + 1} fontSize="18" textAnchor="middle" dominantBaseline="middle">
                {CI_ICONS[h.ci_type] || '📦'}</text>
              <text x={p.x + 36} y={p.y + 20} fill="var(--text-primary)" fontSize="13" fontWeight="600">
                {shortName(h.ci_name || `#${h.ci_id}`, 20)}</text>
              <text x={p.x + 36} y={p.y + 38} fill="var(--text-muted)" fontSize="10">
                {h.ci_type_label || h.ci_type}{h.role ? ` · ${h.role}` : ''}</text>
            </g>
          );
        })}

        {/* ── SERVICE NODE ── */}
        {positions['service'] && (() => {
          const p = positions['service'];
          return (
            <g onMouseDown={(e) => handleMouseDown('service', e)} style={{ cursor: 'move' }}>
              <title>{detail.display_name || detail.name}</title>
              <rect x={p.x} y={p.y} width={NODE_SVC_W} height={NODE_SVC_H} rx={10}
                fill="var(--bg-card)" stroke={dragging === 'service' ? COL_SVC : COL_SVC}
                strokeWidth={dragging === 'service' ? 3 : 2.5} filter="url(#mv-shad)" />
              <text x={p.x + NODE_SVC_W / 2} y={p.y + 22} textAnchor="middle" fill="var(--text-primary)" fontSize="14" fontWeight="700">
                {shortName(detail.display_name || detail.name, 24)}</text>
              <text x={p.x + NODE_SVC_W / 2} y={p.y + 38} textAnchor="middle" fill="var(--text-muted)" fontSize="11">
                {detail.service_type_label} · {detail.status_label}</text>
              <line x1={p.x + 12} y1={p.y + 50} x2={p.x + NODE_SVC_W - 12} y2={p.y + 50} stroke="var(--border)" strokeWidth="1" />
              {accessPoints.length > 0 ? (
                <><text x={p.x + NODE_SVC_W / 2} y={p.y + 66} textAnchor="middle" fill="var(--text-muted)" fontSize="10">Accesos</text>
                  <text x={p.x + NODE_SVC_W / 2} y={p.y + 90} textAnchor="middle" fontSize="20" letterSpacing="5">
                    {accessPoints.slice(0, 7).map(ap => ACC_ICONS[ap.access_type] || '🔗').join(' ')}
                    {accessPoints.length > 7 ? ' …' : ''}</text>
                  <text x={p.x + NODE_SVC_W / 2} y={p.y + 110} textAnchor="middle" fill="var(--text-muted)" fontSize="10">
                    {accessPoints.length} acceso{accessPoints.length !== 1 ? 's' : ''}</text>
                </>
              ) : (
                <text x={p.x + NODE_SVC_W / 2} y={p.y + 80} textAnchor="middle" fill="var(--text-muted)" fontSize="11" fontStyle="italic">Sin accesos</text>
              )}
            </g>
          );
        })()}

        {/* ── DEPS OUT ── */}
        {outNodes.map((d) => {
          const p = positions[d.key];
          if (!p) return null;
          return (
            <g key={d.key} onMouseDown={(e) => handleMouseDown(d.key, e)}
              style={{ cursor: 'move' }}>
              <title>{`${d.name} · ${d.sub}`}</title>
              <rect x={p.x} y={p.y} width={NODE_W} height={NODE_H} rx={8}
                fill="var(--bg-card)" stroke={d.color}
                strokeWidth={dragging === d.key ? 3 : 2} filter="url(#mv-shad)" />
              <text x={p.x + 12} y={p.y + NODE_H / 2 + 1} fontSize="16" textAnchor="middle" dominantBaseline="middle">
                {d.icon}</text>
              <text x={p.x + 32} y={p.y + 20} fill="var(--text-primary)" fontSize="12" fontWeight="600">
                {shortName(d.name, 24)}</text>
              <text x={p.x + 32} y={p.y + 38} fill={d.color} fontSize="10" fontWeight="600">
                {d.sub}</text>
              {d.serviceId && (
                <text x={p.x + NODE_W - 8} y={p.y + NODE_H - 6} textAnchor="end"
                  fill="var(--accent)" fontSize="9" fontWeight="700" style={{ cursor: 'pointer' }}
                  onClick={() => onOpenService?.(d.serviceId)}>abrir ↗</text>
              )}
            </g>
          );
        })}

        {/* ── DEPS IN ── */}
        {inNodes.map((d) => {
          const p = positions[d.key];
          if (!p) return null;
          return (
            <g key={d.key} onMouseDown={(e) => handleMouseDown(d.key, e)}
              style={{ cursor: 'move' }}>
              <title>{`${d.name} · ${d.sub}`}</title>
              <rect x={p.x} y={p.y} width={NODE_W} height={NODE_H} rx={8}
                fill="var(--bg-card)" stroke={d.color}
                strokeWidth={dragging === d.key ? 3 : 2} strokeDasharray={d.dashed ? '5,3' : undefined}
                filter="url(#mv-shad)" />
              <text x={p.x + 12} y={p.y + NODE_H / 2 + 1} fontSize="16" textAnchor="middle" dominantBaseline="middle">
                {d.icon}</text>
              <text x={p.x + 32} y={p.y + 20} fill="var(--text-primary)" fontSize="12" fontWeight="600">
                {shortName(d.name, 24)}</text>
              <text x={p.x + 32} y={p.y + 38} fill={d.color} fontSize="10" fontWeight="600">
                {d.sub}</text>
              {d.serviceId && (
                <text x={p.x + NODE_W - 8} y={p.y + NODE_H - 6} textAnchor="end"
                  fill="var(--accent)" fontSize="9" fontWeight="700" style={{ cursor: 'pointer' }}
                  onClick={() => onOpenService?.(d.serviceId)}>abrir ↗</text>
              )}
            </g>
          );
        })}

        {/* Legend */}
        <text x={maxX - 20} y={maxY - 14} textAnchor="end" fill="var(--text-muted)" fontSize="10">
          <tspan fill={COL_HOST}>━━</tspan> ejecuta · <tspan fill={COL_DEP_OUT}>━━</tspan> necesita · <tspan fill={COL_DEP_IN}>╌╌</tspan> usado por · <tspan fill={COL_REL}>━━</tspan> relación del modelo
        </text>
      </svg>
      </div>
    </div>
  );
}
